import React from 'react';
import { HiExclamationTriangle } from 'react-icons/hi2';

interface UnallocatedTasksWarningModalProps {
  showModal: boolean;
  unallocatedTasksCount: number;
  totalStoryPoints?: number;
  setShowModal: (showModal: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const UnallocatedTasksWarningModal: React.FC<UnallocatedTasksWarningModalProps> = ({
  showModal,
  unallocatedTasksCount,
  totalStoryPoints = 0,
  setShowModal,
  onConfirm,
  onCancel,
}) => {
  const handleClose = () => {
    setShowModal(false);
    onCancel();
  };

  const handleConfirm = () => {
    setShowModal(false);
    onConfirm();
  };

  if (!showModal) return null;

  const storyPointsText = totalStoryPoints > 0 ? ` (${totalStoryPoints} SP)` : '';

  return (
    <div style={{ zIndex: 2147483647 }} className="fixed inset-0 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 mx-4 max-w-md w-full transform transition-all" 
        onClick={(e) => { e.stopPropagation(); }}
      >
        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full">
          <HiExclamationTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
        </div>
        
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
          Unallocated Work Items Need Attention
        </h3>
        
        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-6">
          You have <strong>{unallocatedTasksCount} unallocated work item{unallocatedTasksCount !== 1 ? 's' : ''}{storyPointsText}</strong> that need to be assigned to team members. Please review and allocate them before changing filters or navigating away.
        </p>
        
        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimeout(handleConfirm, 0); }}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400"
          >
            Proceed Anyway
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimeout(handleClose, 0); }}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 flex items-center justify-center min-w-[80px] bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 dark:focus:ring-amber-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnallocatedTasksWarningModal;